import { apiPost } from './api';
import { auth } from './firebase';

// Browser-side failures previously left no trace anywhere. A crash in the SPA
// was invisible unless somebody noticed the symptom and mentioned it, which is
// how the photo-replacement bug surfaced — after it had been broken for months.
//
// Reports go to /api/client-errors and land in Cloud Logging with source=client,
// alongside the server's own structured logs.

// Sending is best-effort and must never itself throw, or an error report can
// trigger the very handler that produced it.
const MAX_REPORTS_PER_SESSION = 25;
let sent = 0;
const seen = new Set();

async function report(message, { stack, context, level } = {}) {
  if (sent >= MAX_REPORTS_PER_SESSION) return;

  // A render loop can fire the same error hundreds of times a second; one
  // report per distinct message is enough to act on.
  const key = `${message}::${(stack || '').slice(0, 200)}`;
  if (seen.has(key)) return;
  seen.add(key);
  sent += 1;

  // Unauthenticated callers are rejected by the endpoint, so don't bother.
  if (!auth.currentUser) return;

  try {
    await apiPost('/api/client-errors', {
      message: String(message ?? 'Unknown error'),
      stack: stack ? String(stack) : null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      context: context ? String(context) : null,
      level: level === 'info' ? 'info' : undefined,
    });
  } catch (e) {
    // Reporting failed — log locally and stop. Retrying a failing reporter is
    // how a broken endpoint turns into a request storm.
    console.warn('[errorReporting] could not report error', e);
  }
}

export function reportError(error, context) {
  report(error?.message || String(error), { stack: error?.stack, context });
}

/**
 * Records something that went *right*, where going right is otherwise silent.
 *
 * The case this exists for: a fault the browser recovers from on its own. It
 * leaves no trace, so the log looks exactly the same as it would had the fault
 * never happened, and "the fix works" becomes indistinguishable from "the bug
 * did not occur". That is not a thought experiment. The invitations-read retry
 * shipped on 2026-09-14 and could not be shown to be working for exactly this
 * reason: six clean sign-ins were consistent both with the retry curing a
 * refusal and with no refusal having happened at all.
 *
 * Deliberately rare. This shares the session cap and the
 * one-report-per-distinct-message rule with error reporting, and it should stay
 * reserved for recoveries that somebody is actually waiting to hear about.
 * Routine successes belong in analytics, which is built for volume; this is
 * built for the handful of events where silence is ambiguous.
 */
export function reportRecovery(message, context) {
  report(message, { context, level: 'info' });
}

export function installErrorReporting() {
  window.addEventListener('error', (event) => {
    report(event.message, {
      stack: event.error?.stack,
      context: `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    report(reason?.message || String(reason), {
      stack: reason?.stack,
      context: 'unhandledrejection',
    });
  });
}
